import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  DateHeader,
  LabelBox,
  LabelDateBox,
  LabelHeader,
  NameHeader,
  TypeBox,
  TypeHeader,
} from "./styles";
import { FileSizeHeader, VersionDetailsBox } from "./styles";
import { styles, ResponsiveArrow } from "./styles";
import { TopContainer } from "./styles";
import { Box, Button, IconButton } from "@mui/material";
import { LinearProgress, Typography } from "@mui/material";
import { Folder, GetFoldersResponse } from "../../proto/api_pb";
import { GetTrackFilesResponse } from "../../proto/api_pb";
import { FileState } from "../../proto/api_pb";
import { TrackFile, GetAssetSizeResponse } from "../../proto/api_pb";
import { RpcError } from "grpc-web";
import { deleteTrackFile, formatFileSize } from "../../services/trackService";
import { fetchUserTrackFiles, getAssetSize } from "../../services/trackService";
import { toast } from "react-toastify";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { TrackFiles } from "../../components/TrackFile/TrackFile";
import StorageComponent from "../../components/StorageComponent/StorageComponent";
import { FileObj, MyFilesProps } from "./types";
import { TrackDeleteConfirmModal } from "../../components/Modal/TrackDeleteConfirmModal";
import CollabModal from "../../components/Modal/CollabModal";
import AddTrackFileModal from "../../components/Modal/AddTrackFileModal";
import { TrackMenuButton } from "../../components/TrackComponents/TrackMenuButton/TrackMenuButton";
import MultipleFileUploadModal from "../../components/Modal/MultipleFilesUploadModal";
import { MyFilesFilters } from "./MyFilesFilters";
import { CreateFolderModal } from "../../components/Modal/CreateFolderModal";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { FoldersSection } from "../../components/FoldersSection/FoldersSection";
import { useLocation } from "react-router-dom";
import { getAllFolders } from "../../services/folderService";

const MyFiles: React.FC<MyFilesProps> = ({
  getStorageUsed,
  setUploadingFilesDetailsList,
  setIsUploading,
  setUploadingCardsStatusList,
  setBreadCrumbs,
}) => {
  const { userId } = useContext(AuthContext);
  const [files, setFiles] = useState<FileObj[]>([]);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isNameSortAsc, setIsNameSortAsc] = useState(true);
  const [isDateSortAsc, setIsDateSortAsc] = useState(true);
  const [isFileLabelSortAsc, setIsFileLabelSortAsc] = useState(true);
  const [isFileSizeSortAsc, setIsFileSizeSortAsc] = useState(true);
  const [isFileTypeSortAsc, setIsFileTypeSortAsc] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [fileToDeleteId, setFileToDeleteId] = useState("");
  const [openCollabsModal, setOpenCollabsModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<TrackFile>();
  const [openAddTrackFileModal, setOpenAddTrackFileModal] = useState(false);
  const [isFileAdded, setIsFileAdded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileObj[]>([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isMultipleFileUploadModalOpen, setIsMultipleFileUploadModal] =
    useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] =
    useState<boolean>(false);
  const [foldersList, setFoldersList] = useState<Folder[]>([]);
  const location = useLocation();
  const isMyFilesPage = location.pathname === "/my-files";
  const { getValidAccessToken } = useGetValidAccessToken();

  const getAllFoldersList = async () => {
    const token = await getValidAccessToken();
    const res = await getAllFolders(token, userId);
    if (res instanceof GetFoldersResponse) {
      if (isMyFilesPage) {
        setFoldersList(
          res
            .getFoldersList()
            .filter(
              (folder) =>
                folder.getParentFolderId() === "" &&
                folder.getFolderState() ===
                  (FileState.FILE_STATE_ACTIVE ||
                    FileState.FILE_STATE_UNDEFINED)
            )
        );
      }
    }
  };

  const handleFetchTrackFiles = useCallback(async () => {
    setPageLoaded(false);
    const token = await getValidAccessToken();
    try {
      const response = (await fetchUserTrackFiles(
        token,
        userId
      )) as GetTrackFilesResponse;
      const filesList = response.getTrackFilesList();
      const filesObjList: FileObj[] = [];
      for (const file of filesList) {
        if (
          file.getFileState() === FileState.FILE_STATE_ACTIVE &&
          file.getFolderId() === ""
        ) {
          try {
            const fileSize = (await getAssetSize(
              token,
              file.getFileUrl()
            )) as GetAssetSizeResponse;

            filesObjList.push({
              trackFile: file,
              fileSize: fileSize.getSizeGb() ? fileSize.getSizeGb() : 0,
            });
          } catch (error) {
            console.log(error);
          }
        }
      }
      setFiles([...filesObjList]);
      setPageLoaded(true);
    } catch (e) {
      const error = e as RpcError;
      console.log(error.code);
      console.log(error.message);
    }
    // eslint-disable-next-line
  }, [getValidAccessToken, userId, isFileAdded]);

  useEffect(() => {
    setBreadCrumbs([]);
    if (userId !== "") {
      handleFetchTrackFiles();
    }
  }, [userId, getValidAccessToken, handleFetchTrackFiles, setBreadCrumbs]);

  const handleDateSort = () => {
    if (isDateSortAsc) {
      const sortedFiles = [...files].sort(
        (fileA, fileB) =>
          fileA.trackFile.getTimestamp() - fileB.trackFile.getTimestamp()
      );
      setFiles([...sortedFiles]);
      setIsDateSortAsc(false);
    } else {
      const sortedFiles = [...files].sort(
        (fileA, fileB) =>
          fileB.trackFile.getTimestamp() - fileA.trackFile.getTimestamp()
      );
      setFiles([...sortedFiles]);
      setIsDateSortAsc(true);
    }
  };
  const handleNameSort = () => {
    if (isNameSortAsc) {
      const sortedFiles = [...files].sort((fileA, fileB) =>
        fileB.trackFile
          .getFilename()
          .localeCompare(fileA.trackFile.getFilename())
      );
      setFiles([...sortedFiles]);
      setIsNameSortAsc(false);
    } else {
      const sortedFiles = [...files].sort((fileA, fileB) =>
        fileA.trackFile
          .getFilename()
          .localeCompare(fileB.trackFile.getFilename())
      );
      setFiles([...sortedFiles]);
      setIsNameSortAsc(true);
    }
  };

  const handleTypeSort = () => {
    if (isFileTypeSortAsc) {
      const sortedFiles = [...files].sort(
        (fileA, fileB) => fileA.trackFile.getType() - fileB.trackFile.getType()
      );
      setFiles([...sortedFiles]);
      setIsFileTypeSortAsc(false);
    } else {
      const sortedFiles = [...files].sort(
        (fileA, fileB) => fileB.trackFile.getType() - fileA.trackFile.getType()
      );
      setFiles([...sortedFiles]);
      setIsFileTypeSortAsc(true);
    }
  };

  const handleLabelSort = () => {
    if (isFileLabelSortAsc) {
      const sortedFiles = [...files].sort((fileA, fileB) =>
        fileB.trackFile.getLabel().localeCompare(fileA.trackFile.getLabel())
      );
      setFiles([...sortedFiles]);
      setIsFileLabelSortAsc(false);
    } else {
      const sortedFiles = [...files].sort((fileA, fileB) =>
        fileA.trackFile.getLabel().localeCompare(fileB.trackFile.getLabel())
      );
      setFiles([...sortedFiles]);
      setIsFileLabelSortAsc(true);
    }
  };

  const handleFileSizeSort = () => {
    if (isFileSizeSortAsc) {
      const sortedFiles = [...files].sort(
        (fileA, fileB) => fileA.fileSize - fileB.fileSize
      );
      setFiles([...sortedFiles]);
      setIsFileSizeSortAsc(false);
    } else {
      const sortedFiles = [...files].sort(
        (fileA, fileB) => fileB.fileSize - fileA.fileSize
      );
      setFiles([...sortedFiles]);
      setIsFileSizeSortAsc(true);
    }
  };

  const handleOpenDeleteModal = (id: string) => {
    setIsOpen(true);
    setFileToDeleteId(id);
  };
  const handleCloseDeleteModal = () => {
    setIsOpen(false);
  };

  const handleDeleteFile = async () => {
    const updatedFiles = files.filter(
      (file) => file.trackFile.getId() !== fileToDeleteId
    );
    setFiles([...updatedFiles]);
    const deleteTrackFileResponse = await deleteTrackFile(
      fileToDeleteId,
      await getValidAccessToken()
    );
    if (deleteTrackFileResponse instanceof RpcError) {
      throw deleteTrackFileResponse;
    }
    handleCloseDeleteModal();
    await getStorageUsed();
    toast.success("File removed successfully");
  };

  const handleCloseCollabsModal = () => {
    setOpenCollabsModal(false);
  };

  const handleOpenCollabsModal = (id: string) => {
    const fl = files.filter((f) => f.trackFile.getId() === id)[0];
    setSelectedFile(fl.trackFile);
    setOpenCollabsModal(true);
  };

  const handleFileAdded = () => {
    getStorageUsed();
    handleFetchTrackFiles();
    setIsFileAdded((prev) => !prev);
  };

  const handleCloseAddFileModal = (isAdded: boolean, message: string) => {
    if (isAdded) {
      toast.info("File Successfully Added");
    }
    setOpenAddTrackFileModal(false);
  };

  const handleAddFile = () => {
    setOpenAddTrackFileModal(true);
  };

  const handleCloseMultipleFileUploadModal = () => {
    setIsMultipleFileUploadModal(false);
  };

  const handleAddFiles = () => {
    setIsMultipleFileUploadModal(true);
  };

  useEffect(() => {
    setSelectedFiles(files);
  }, [files]);

  const handleDragStart = (index: any) => {
    setDraggedIndex(index);
  };

  return (
    <>
      {isCreateFolderModalOpen && (
        <CreateFolderModal
          setIsCreateFolderModalOpen={setIsCreateFolderModalOpen}
          isCreateFolderModalOpen={isCreateFolderModalOpen}
          getAllFoldersList={getAllFoldersList}
        />
      )}
      {isMultipleFileUploadModalOpen && (
        <MultipleFileUploadModal
          open={isMultipleFileUploadModalOpen}
          handleClose={handleCloseMultipleFileUploadModal}
          handleFileAdded={handleFileAdded}
          setUploadingFilesDetailsList={setUploadingFilesDetailsList}
          setIsUploading={setIsUploading}
          setUploadingCardsStatusList={setUploadingCardsStatusList}
        />
      )}
      <TrackDeleteConfirmModal
        deleteConfirmModalOpen={isOpen}
        closeDeleteConfirmModal={handleCloseDeleteModal}
        handleDeleteTrack={handleDeleteFile}
        type="File"
      />
      {openCollabsModal && (
        <CollabModal
          openCollabsModal={openCollabsModal}
          handleCloseCollabsModal={handleCloseCollabsModal}
          trackToEdit={selectedFile}
          handleFetchTrackFiles={handleFetchTrackFiles}
        />
      )}
      {openAddTrackFileModal && (
        <AddTrackFileModal
          open={openAddTrackFileModal}
          handleClose={handleCloseAddFileModal}
          handleFileAdded={handleFileAdded}
        />
      )}
      <Box style={styles.libraryContainer}>
        {!pageLoaded && (
          <Box sx={styles.linearProgressContainer}>
            <LinearProgress color="inherit" />
          </Box>
        )}
        {pageLoaded && (
          <>
            <Box style={styles.headerContainer}>
              <h1 style={styles.header}>Organize & Store Your Files</h1>
            </Box>
            <TopContainer>
              <TrackMenuButton
                handleAddTrack={handleAddFile}
                handleAddTracks={handleAddFiles}
                isMultipleFileUploadButton={true}
              />
              <Button
                onClick={() =>
                  setIsCreateFolderModalOpen(!isCreateFolderModalOpen)
                }
                sx={styles.button}
              >
                <CreateNewFolderIcon
                  sx={{ width: "20px", margin: "5px", marginTop: "2px" }}
                />
                Create Folder
              </Button>
              <StorageComponent />
            </TopContainer>
            <Box sx={styles.filtersContainer}>
              <MyFilesFilters
                files={files}
                setSelectedFiles={setSelectedFiles}
                selectedFiles={selectedFiles}
              />
            </Box>
            <FoldersSection
              draggedIndex={draggedIndex}
              selectedFiles={selectedFiles}
              files={files}
              getAllFoldersList={getAllFoldersList}
              foldersList={foldersList}
              handleFetchTrackFiles={handleFetchTrackFiles}
            />
            <Typography sx={{ marginLeft: "10px" }} variant="h4">
              Files
            </Typography>
            <Box sx={styles.tableHeader}>
              <VersionDetailsBox>
                <Box style={styles.ownerLabelStyle}>Owner</Box>
                <LabelDateBox>
                  <Box style={styles.nameContainer}>
                    <NameHeader noWrap>Name</NameHeader>
                    <IconButton onClick={handleNameSort}>
                      {isNameSortAsc && (
                        <ArrowUpwardIcon style={{ width: 20 }} />
                      )}
                      {!isNameSortAsc && (
                        <ArrowDownwardIcon style={{ width: 20 }} />
                      )}
                    </IconButton>
                  </Box>
                  <LabelBox>
                    <LabelHeader>Label</LabelHeader>
                    <ResponsiveArrow onClick={handleLabelSort}>
                      {isFileLabelSortAsc && (
                        <ArrowUpwardIcon style={{ width: 20 }} />
                      )}
                      {!isFileLabelSortAsc && (
                        <ArrowDownwardIcon style={{ width: 20 }} />
                      )}
                    </ResponsiveArrow>
                  </LabelBox>
                  <TypeBox>
                    <TypeHeader>Type</TypeHeader>
                    <ResponsiveArrow onClick={handleTypeSort}>
                      {isFileTypeSortAsc && (
                        <ArrowUpwardIcon style={{ width: 20 }} />
                      )}
                      {!isFileTypeSortAsc && (
                        <ArrowDownwardIcon style={{ width: 20 }} />
                      )}
                    </ResponsiveArrow>
                  </TypeBox>
                  <Box style={styles.dateContainer}>
                    <DateHeader noWrap>Date Created</DateHeader>
                    <IconButton onClick={handleDateSort}>
                      {isDateSortAsc && (
                        <ArrowUpwardIcon style={{ width: 20 }} />
                      )}
                      {!isDateSortAsc && (
                        <ArrowDownwardIcon style={{ width: 20 }} />
                      )}
                    </IconButton>
                  </Box>
                </LabelDateBox>
                <Box style={styles.fileSizeContainer}>
                  <FileSizeHeader>File Size</FileSizeHeader>
                  <ResponsiveArrow onClick={handleFileSizeSort}>
                    {isFileSizeSortAsc && (
                      <ArrowUpwardIcon style={{ width: 20 }} />
                    )}
                    {!isFileSizeSortAsc && (
                      <ArrowDownwardIcon style={{ width: 20 }} />
                    )}
                  </ResponsiveArrow>
                </Box>
              </VersionDetailsBox>
              {selectedFiles.map((el: FileObj, i) => {
                return (
                  <div key={i} draggable onDragStart={() => handleDragStart(i)}>
                    <TrackFiles
                      url={el.trackFile.getFileUrl()}
                      label={el.trackFile.getLabel()}
                      timeStamp={el.trackFile.getTimestamp()}
                      ownerId={el.trackFile.getOwnerId()}
                      fileName={el.trackFile.getFilename()}
                      fileType={el.trackFile.getType()}
                      fileSize={el.fileSize ? formatFileSize(el.fileSize) : "-"}
                      handleOpenDeleteModal={handleOpenDeleteModal}
                      fileId={el.trackFile.getId()}
                      handleOpenCollabsModal={handleOpenCollabsModal}
                      collaborators={el.trackFile.getCollaboratorsList()}
                      file={el.trackFile}
                    />
                  </div>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default MyFiles;
